type DateLike = Date | { toDate?: () => Date; seconds?: number } | null | undefined;

export interface StatsContentItem {
  id: string;
  title: string;
  language?: string;
  type?: string;
  archived?: boolean;
  hourValue?: number;
}

export interface StatsAssignment {
  contentId: string;
  user: string;
  requestedBy?: string;
  completed?: boolean;
  viewedAt?: DateLike;
  completedAt?: DateLike;
}

export interface StatsFilters {
  from?: Date | null;
  to?: Date | null;
  language?: string | null;
  coachId?: string | null;
}

export interface ScreencastRow {
  contentId: string;
  title: string;
  language: string;
  type: string;
  archived: boolean;
  assignedByCoach: number;
  selfStarted: number;
  viewed: number;
  completed: number;
  participants: number;
  completionRate: number;
  hoursCredited: number;
  lastActivity: Date | null;
}

export interface CoachRow {
  coachId: string;
  participants: number;
  assignedByCoach: number;
  viewed: number;
  completed: number;
}

export interface ScreencastStats {
  rows: ScreencastRow[];
  coaches: CoachRow[];
  totals: {
    assignments: number;
    viewed: number;
    completed: number;
    participants: number;
    completionRate: number;
    hoursCredited: number;
  };
}

export function toDate(value: DateLike): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
}

function rate(part: number, total: number): number {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}

/** Most recent meaningful moment of an assignment: completion, else first view. */
function activityDate(assignment: StatsAssignment): Date | null {
  return toDate(assignment.completedAt) || toDate(assignment.viewedAt);
}

function inDateRange(assignment: StatsAssignment, from?: Date | null, to?: Date | null): boolean {
  if (!from && !to) return true;
  const date = activityDate(assignment);
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

/**
 * Aggregates content-assignments per screencast and per coach.
 * Only assignments of participants are counted (coach/admin test views are ignored).
 * `coachByParticipant` maps a participant id to the coach of their current dossier.
 */
export function computeScreencastStats(
  items: StatsContentItem[],
  assignments: StatsAssignment[],
  participantIds: Set<string>,
  coachByParticipant: Record<string, string>,
  filters: StatsFilters = {}
): ScreencastStats {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const language = filters.language || null;

  const relevant = assignments.filter((a) => {
    const item = itemsById.get(a.contentId);
    if (!item) return false;
    if (!participantIds.has(a.user)) return false;
    if (language && (item.language || '') !== language) return false;
    if (filters.coachId && coachByParticipant[a.user] !== filters.coachId) return false;
    return inDateRange(a, filters.from, filters.to);
  });

  const rowsById = new Map<string, ScreencastRow & { users: Set<string> }>();
  const coachesById = new Map<string, CoachRow & { users: Set<string> }>();
  const allUsers = new Set<string>();
  let viewedTotal = 0;
  let completedTotal = 0;
  let hoursTotal = 0;

  for (const a of relevant) {
    const item = itemsById.get(a.contentId)!;
    let row = rowsById.get(item.id);
    if (!row) {
      row = {
        contentId: item.id,
        title: item.title,
        language: item.language || '',
        type: item.type || '',
        archived: item.archived === true,
        assignedByCoach: 0,
        selfStarted: 0,
        viewed: 0,
        completed: 0,
        participants: 0,
        completionRate: 0,
        hoursCredited: 0,
        lastActivity: null,
        users: new Set(),
      };
      rowsById.set(item.id, row);
    }

    const selfStarted = !a.requestedBy || a.requestedBy === a.user;
    const viewed = !!toDate(a.viewedAt) || a.completed === true;
    const completed = a.completed === true;
    const hours = completed ? item.hourValue || 1 : 0;

    if (selfStarted) row.selfStarted++;
    else row.assignedByCoach++;
    if (viewed) row.viewed++;
    if (completed) row.completed++;
    row.hoursCredited += hours;
    row.users.add(a.user);
    const date = activityDate(a);
    if (date && (!row.lastActivity || date > row.lastActivity)) row.lastActivity = date;

    allUsers.add(a.user);
    if (viewed) viewedTotal++;
    if (completed) completedTotal++;
    hoursTotal += hours;

    const coachId = coachByParticipant[a.user];
    if (coachId) {
      let coach = coachesById.get(coachId);
      if (!coach) {
        coach = { coachId, participants: 0, assignedByCoach: 0, viewed: 0, completed: 0, users: new Set() };
        coachesById.set(coachId, coach);
      }
      if (!selfStarted) coach.assignedByCoach++;
      if (viewed) coach.viewed++;
      if (completed) coach.completed++;
      coach.users.add(a.user);
    }
  }

  const rows: ScreencastRow[] = Array.from(rowsById.values())
    .map(({ users, ...row }) => ({
      ...row,
      participants: users.size,
      completionRate: rate(row.completed, row.assignedByCoach + row.selfStarted),
    }))
    .sort((a, b) => b.viewed - a.viewed || a.title.localeCompare(b.title));

  const coaches: CoachRow[] = Array.from(coachesById.values())
    .map(({ users, ...coach }) => ({ ...coach, participants: users.size }))
    .sort((a, b) => b.completed - a.completed);

  return {
    rows,
    coaches,
    totals: {
      assignments: relevant.length,
      viewed: viewedTotal,
      completed: completedTotal,
      participants: allUsers.size,
      completionRate: rate(completedTotal, relevant.length),
      hoursCredited: hoursTotal,
    },
  };
}
